/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { Result } from "../../network/http-client"
import type { ViewModel } from "../../viewmodel/viewmodel"

import type { UserDetailService } from "./user-detail-service"
import type { UserDetailStore } from "./user-detail-store"
import type { UserRoleService } from "./user-role-service"
import type { UserDetail, UserList } from "./user-types"
import { UserRole } from "./user-types"

export class UserDetailViewModel implements ViewModel {
    name: string = this.constructor.name

    constructor(
        private readonly userId: number,
        private readonly detailStore: UserDetailStore,
        private readonly userDetailService: UserDetailService,
        private readonly userRoleService: UserRoleService
    ) {}

    isValidUserRole = (value: string | null): value is UserRole => {
        return Object.values(UserRole).includes(value as UserRole)
    }

    // Getters
    get userDetail(): UserList | null {
        const { userDetail } = this.detailStore
        return userDetail?.id === this.userId ? userDetail : null
    }

    get userDetailRoles(): UserRole[] {
        return this.detailStore.userDetailRoles
    }

    // Service calls
    async getUserDetail(): Promise<void> {
        await this.userDetailService.getOrganizationUser(this.userId)
        await this.userRoleService.getUserRoles(this.userId)
    }

    async assignUserRole(role: UserRole): Promise<Result<void>> {
        return await this.userRoleService.assignUserRole(this.userId, role)
    }

    async removeUserRole(role: UserRole): Promise<Result<void>> {
        return await this.userRoleService.removeUserRole(this.userId, role)
    }

    async createOrganizationUser(
        name: string,
        username: string,
        orgId: number,
        roles: UserRole[],
        applicationTypeIds: number[]
    ): Promise<Result<UserDetail>> {
        return await this.userDetailService.createOrganizationUser(name, username, orgId, roles, applicationTypeIds)
    }

    async updateOrganizationUser(name: string): Promise<Result<UserDetail>> {
        return await this.userDetailService.updateOrganizationUser(this.userId, name)
    }

    async resetUserPassword(): Promise<Result<void>> {
        return await this.userDetailService.resetUserPassword(this.userId)
    }

    async deleteOrganizationUser(): Promise<Result<void>> {
        return await this.userDetailService.deleteOrganizationUser(this.userId)
    }
}
